import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { GetAllPinnedItemsResponse, PinnedItem } from "./pinned-models";
import { PinnedService } from "./pinned.service";
import { WorkspacesService } from "../workspaces.service";
import { Company } from "src/app/dashboard/shared-data.service";
import { HotToastService } from "@ngneat/hot-toast";
import { NgClass } from "@angular/common";
import { PinnedCardComponent } from "./pinned-card/pinned-card.component";
import { PinFilterComponent as PinFilterComponent_1 } from "../../../components/pin-filter/pin-filter.component";
import { PaidOverlayDirective } from "../../../paid-overlay.directive";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RealTimeService } from "../../common/notifications/real-time.service";
import { Conversation } from "src/app/dashboard/conversations/panels/conversation.model";

@Component({
  selector: "app-pinned-panel",
  templateUrl: "./pinned-panel.component.html",
  styleUrls: ["./pinned-panel.component.scss"],
  imports: [
    PaidOverlayDirective,
    PinFilterComponent_1,
    NgClass,
    PinnedCardComponent,
  ],
})
export class PinnedPanelComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  pinnedItems: PinnedItem[];
  company: Company;
  conversation: Conversation;
  filter: string = "all";
  isPanelHidden = false;

  readonly destroyRef = inject(DestroyRef);
  realtimeUpdateSubscriptions = [];
  constructor(
    private pinnedService: PinnedService,
    private workspacesService: WorkspacesService,
    private toastr: HotToastService,
    private cdr: ChangeDetectorRef,
    private realTimeService: RealTimeService,
  ) {
    this.pinnedService.newPinAdded$.subscribe((pinnedItem) => {
      this.reload();
    });
  }

  ngOnInit(): void {
    this.pinnedService.sendNewPinnedItemToComponent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ pinned_item }) => {
        this.pinnedItems.push(pinned_item);
        this.cdr.detectChanges();
      });

    this.pinnedService.unpinItemSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pinnedItemUuid: string) => {
        this.pinnedItems = this.pinnedItems.filter(
          ({ uuid }) => uuid !== pinnedItemUuid,
        );
        this.cdr.detectChanges();
      });

    this.workspacesService.$company
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => {
        if (company != null) {
          console.log("$company loaded in pinned", company);
          this.company = company;
          this.reload();
        }
      });
    this.workspacesService.$conversation
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((conversation) => {
        if (conversation != null) {
          this.conversation = conversation;
          this.realTimeService.setConversationUpdatesListener(
            this.conversation.uuid,
          );

          this.realtimeUpdateUnsubscribe();

          this.realtimeUpdateSubscriptions.push(
            this.realTimeService.conversationUpdates$.subscribe(
              (message: any) => {
                this.pinnedItems = this.pinnedItems.map((pinnedItem) => {
                  if (
                    pinnedItem.content_object.uuid ===
                    message?.tracked_item.uuid
                  ) {
                    return {
                      ...pinnedItem,
                      content_object: message.tracked_item,
                    };
                  }
                  return pinnedItem;
                });
                this.cdr.detectChanges();
              },
            ),
          );
        }
      });
  }

  realtimeUpdateUnsubscribe() {
    this.realtimeUpdateSubscriptions.forEach((sub) => {
      sub.unsubscribe();
    });

    this.realtimeUpdateSubscriptions = [];
  }

  ngOnDestroy(): void {}

  async getAllPinnedItems(filter: string): Promise<GetAllPinnedItemsResponse> {
    if (!this.company) return;
    const res = await this.pinnedService.getAllPinnedItems(
      this.company.uuid,
      filter,
    );

    if (res != null) {
      return { pinned_items: res.pinned_items };
    }

    return null;
  }

  async reload(): Promise<void> {
    this.loading = true;
    this.pinnedItems = [];
    this.cdr.detectChanges();
    const res = await this.getAllPinnedItems(this.filter);

    if (res != null) {
      this.pinnedItems = res.pinned_items;
    }

    this.loading = false;
    this.cdr.detectChanges();
  }

  async unpinItem(itemUuid: string) {
    if (this.pinnedItems.length > 0) {
      this.pinnedItems = this.pinnedItems.filter(
        ({ uuid }) => uuid !== itemUuid,
      );
    }
    this.toastr.success("Item unpinned succesffully");
    const { response } = await this.pinnedService.updatePin(
      itemUuid,
      this.company.uuid,
    );
  }

  pinFilterChanged(filter: string) {
    this.filter = filter;
    this.reload();
  }

  togglePanel(event) {
    var target = event.target || event.srcElement || event.currentTarget;
    target.closest("app-pinned-panel").classList.toggle("basis-auto");
    this.isPanelHidden = !this.isPanelHidden;
  }
}
